import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { FlowpiDialogSurface } from "../ui/flowpi-dialog";
import { Button } from "../ui/button";
import QuotationConfirmationDialog from "./QuotationConfirmationDialog";
import { listEligibleServiceTickets } from "../../lib/quotationApi";

const BRANCHES = ["jakarta", "surabaya", "bali"];

export default function ServiceTicketPickerDialog({
  open,
  existingTickets = [],
  additionalCandidates = [],
  initialBranch = "jakarta",
  quotationId = "",
  onClose,
  onConfirm,
}) {
  const [branch, setBranch] = useState("jakarta");
  const [search, setSearch] = useState("");
  const [result, setResult] = useState({ data: [] });
  const [selected, setSelected] = useState([]);
  const [pendingBranch, setPendingBranch] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const requestId = useRef(0);

  useEffect(() => {
    if (!open) return;
    setSelected(existingTickets);
    setBranch(quotationId ? initialBranch : existingTickets[0]?.branch || initialBranch);
    setSearch("");
    setResult({ data: [] });
    setPendingBranch("");
    setApplying(false);
  }, [open, existingTickets, initialBranch, quotationId]);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    const id = ++requestId.current;
    setLoading(true);
    setError("");
    listEligibleServiceTickets({ branch, ...(quotationId ? { quotation_id: quotationId } : {}) }, { signal: controller.signal })
      .then((data) => {
        if (id === requestId.current) setResult(data);
      })
      .catch((requestError) => {
        if (
          id === requestId.current &&
          requestError?.code !== "ERR_CANCELED" &&
          requestError?.name !== "CanceledError"
        ) {
          setError(
            requestError?.response?.data?.detail?.error?.message ||
              "Eligible Service Tickets could not be loaded."
          );
        }
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
    return () => controller.abort();
  }, [open, branch, quotationId]);

  const selectedIds = useMemo(
    () => new Set(selected.map((ticket) => ticket.service_ticket_id)),
    [selected]
  );
  const orderedCandidates = useMemo(() => {
    const groups = { queue: [], pending: [], other: [] };
    const unique = new Map();
    for (const ticket of [...(additionalCandidates || []), ...(result.data || [])]) {
      if (ticket.branch && ticket.branch !== branch) continue;
      if (!unique.has(ticket.service_ticket_id)) unique.set(ticket.service_ticket_id, ticket);
    }
    for (const ticket of unique.values()) {
      if (ticket.stage === "queue") groups.queue.push(ticket);
      else if (ticket.stage === "pending") groups.pending.push(ticket);
      else groups.other.push(ticket);
    }
    return [...groups.queue, ...groups.pending, ...groups.other];
  }, [additionalCandidates, branch, result.data]);
  const filteredAvailable = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return orderedCandidates.filter((ticket) => {
      if (selectedIds.has(ticket.service_ticket_id)) return false;
      if (!query) return true;
      return [ticket.service_ticket_number, ticket.customer_name, ticket.serial_number]
        .some((value) => String(value || "").toLocaleLowerCase().includes(query));
    });
  }, [orderedCandidates, search, selectedIds]);
  const toggle = (ticket, checked) => {
    setSelected((current) =>
      checked
        ? [...current.filter((entry) => entry.service_ticket_id !== ticket.service_ticket_id), ticket]
        : current.filter((entry) => entry.service_ticket_id !== ticket.service_ticket_id)
    );
  };
  const applySelection = async () => {
    if (applying || selected.length < 2) return;
    setApplying(true);
    try {
      await onConfirm(selected, branch);
    } finally {
      setApplying(false);
    }
  };
  const switchBranch = (nextBranch) => {
    setBranch(nextBranch);
    setSelected([]);
    setSearch("");
    setResult({ data: [] });
    setPendingBranch("");
  };
  const requestBranchChange = (nextBranch) => {
    if (quotationId) return;
    if (nextBranch === branch) return;
    if (selected.length) setPendingBranch(nextBranch);
    else switchBranch(nextBranch);
  };

  if (!open) return null;
  return <>
    <FlowpiDialogSurface open={open} onClose={onClose} busy={applying} size="workflow" accessibleTitle="Add Service Ticket" testId="service-ticket-picker">
        <section className="flex h-[min(48rem,calc(100dvh-2rem))] min-w-0 w-full max-w-5xl flex-col overflow-hidden rounded-lg border bg-card shadow-brutal">
          <header className="flex shrink-0 items-start justify-between gap-4 border-b p-5">
            <div>
              <h2 className="text-xl font-semibold">Add Service Ticket</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Select service tickets from Queue or Pending stage.
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close ticket picker">
              <X />
            </Button>
          </header>
          <div
            className="min-w-0 shrink-0 px-5 pt-5"
            data-testid="ticket-picker-controls"
          >
            <div className="flex gap-6 border-b">
              {BRANCHES.map((value) => (
                <button
                  type="button"
                  key={value}
                  className={`border-b-2 px-2 pb-3 text-sm font-medium capitalize ${
                    branch === value
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground"
                  }`}
                  disabled={Boolean(quotationId)}
                  onClick={() => requestBranchChange(value)}
                >
                  {value}
                </button>
              ))}
            </div>
            <label className="relative mt-4 block max-w-xl">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              />
              <input
                className="w-full rounded-[calc(var(--radius)-0.25rem)] border border-input bg-card py-2.5 pl-10 pr-3 text-foreground outline-none focus:border-ring focus:ring-[3px] focus:ring-ring/15"
                value={search}
                maxLength={200}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search ticket number, customer, or serial number..."
                aria-label="Search eligible tickets"
              />
            </label>
          </div>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col px-5 pb-5">
            <div
              className="min-h-0 min-w-0 flex-1 overflow-y-auto"
              data-testid="ticket-picker-results"
            >
              <TicketTable
                title={`Selected (${selected.length})`}
                tickets={selected}
                selected
                onToggle={toggle}
              />
              <TicketTable
                title={`Available Tickets (${filteredAvailable.length})`}
                tickets={filteredAvailable}
                onToggle={toggle}
                loading={loading}
                error={error}
              />
            </div>
          </div>
          <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t p-5">
            <p className="text-sm">
              <span className="font-semibold text-primary">{selected.length}</span> tickets selected
            </p>
            <div className="flex gap-2">
              <Button variant="cancel" onClick={onClose}>Cancel</Button>
              <Button
                variant="success"
                disabled={selected.length < 2 || applying}
                onClick={applySelection}
              >
                {applying ? "Applying..." : "Apply Selection"}
              </Button>
            </div>
          </footer>
        </section>
    </FlowpiDialogSurface>
    <QuotationConfirmationDialog
      open={Boolean(pendingBranch)}
      title="Change Branch?"
      message="Changing Branch will clear the Service Tickets currently selected in this picker."
      confirmLabel="Change Branch"
      eyebrow="Selection change"
      onCancel={() => setPendingBranch("")}
      onConfirm={() => pendingBranch && switchBranch(pendingBranch)}
      testid="picker-branch-change-confirmation"
    />
  </>;
}

function TicketTable({ title, tickets, selected = false, onToggle, loading, error }) {
  return (
    <section className="mt-4 min-w-0">
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      <div className="w-full max-w-full overflow-x-auto rounded-lg border">
        <table className="wms-table min-w-[760px]">
          <thead>
            <tr>
              <th>Select</th>
              <th>Stage</th>
              <th>Ticket Number</th>
              <th>Customer</th>
              <th>Serial Number</th>
            </tr>
          </thead>
          <tbody>
            {loading && !tickets.length ? (
              <tr><td colSpan="5" className="py-5 text-center text-muted-foreground">Loading tickets...</td></tr>
            ) : error && !tickets.length ? (
              <tr><td colSpan="5" className="py-5 text-center text-destructive">{error}</td></tr>
            ) : !tickets.length ? (
              <tr><td colSpan="5" className="py-5 text-center text-muted-foreground">No tickets.</td></tr>
            ) : tickets.map((ticket) => (
              <tr key={ticket.service_ticket_id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={(event) => onToggle(ticket, event.target.checked)}
                    aria-label={`Select ${ticket.service_ticket_number}`}
                  />
                </td>
                <td><span className="capitalize">{ticket.stage}</span></td>
                <td>{ticket.service_ticket_number}</td>
                <td>{ticket.customer_name || ticket.customer?.company_store_name || ""}</td>
                <td>{ticket.serial_number}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
